import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Clock, BookOpen } from 'lucide-react';
import TemplateSelector from '@/components/utils/TemplateSelector';
import { getTemplates } from '@/actions/templates';
// Template interface based on the pattern used in other components
interface Template {
	_id: string;
	title?: string;
	text: string;
	type?: string;
	language?: string;
	isFavorite?: boolean;
	createdAt?: string;
}

export interface EditableStep {
	text: string;
	delayDays: number;
	atTime: string; // HH:mm local
	// Only meaningful on the first step: send as soon as the customer is subscribed
	sendImmediately?: boolean;
}

interface Props {
	index: number;
	step: EditableStep;
	onChange: (next: EditableStep) => void;
}

const SequenceStepEditor: React.FC<Props> = ({ index, step, onChange }) => {
	const dispatch = useDispatch();
	const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);
	const templates = useSelector(
		(state: { templates?: Template[] }) => state.templates || []
	) as Template[];

	const handleTemplateClick = ({ template }: { template: Template }) => {
		setShowTemplatesDialog(false);

		// Apply template text to the step
		let newText = template.text || '';

		// Remove ((image)) tags since sequence steps don't support images yet
		newText = newText.replace(/\(\(image\)\)/gi, '').trim();

		onChange({ ...step, text: newText });
	};

	const handleDialogOpenChange = (open: boolean) => {
		setShowTemplatesDialog(open);
		// Dialog will auto-reset via TemplateSelector's internal useEffect
	};

	const getUserTemplates = async () => {
		dispatch(getTemplates() as any);
	};

	const deleteTemplate = async ({ template }: { template: Template }) => {
		try {
			const res = await axios.post('/api/template/delete', {
				template,
			});

			dispatch(getTemplates() as any);

			if (res.data && res.data.error && res.data.message !== 'No template found') {
				console.error(res.data.message || 'There was an error deleting your template.');
			}
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<div className="rounded-xl border shadow-sm bg-background p-3">
			<div className="grid gap-2">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<Label htmlFor={`message-${index}`}>{`Message ${
							index + 1
						}`}</Label>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setShowTemplatesDialog(true)}
							className="h-7 px-2 gap-1.5 text-xs font-normal text-muted-foreground hover:text-foreground"
							title="Use a template for this message"
						>
							<BookOpen className="h-3.5 w-3.5" />
							Use template
						</Button>
					</div>
				</div>
				<Textarea
					id={`message-${index}`}
					placeholder="Write the message for this step"
					value={step.text}
					onChange={(e) =>
						onChange({ ...step, text: e.target.value })
					}
				/>
				<div className="flex flex-wrap items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-sm">
					<Clock className="h-4 w-4" />
					<span>{index === 0 ? 'Send' : 'Then send'}</span>
					{index === 0 && (
						<Select
							value={
								step.sendImmediately
									? 'immediately'
									: 'schedule'
							}
							onValueChange={(v) =>
								onChange({
									...step,
									sendImmediately: v === 'immediately',
								})
							}
						>
							<SelectTrigger className="h-7 w-[150px] rounded-full bg-background">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="immediately">
									Immediately
								</SelectItem>
								<SelectItem value="schedule">
									On a schedule
								</SelectItem>
							</SelectContent>
						</Select>
					)}
					{!(index === 0 && step.sendImmediately) && (
						<>
							<Input
								type="number"
								min={0}
								value={step.delayDays}
								onChange={(e) =>
									onChange({
										...step,
										delayDays: Number(e.target.value || 0),
									})
								}
								className="h-7 w-16 rounded-full bg-background"
							/>
							<span>
								day{step.delayDays === 1 ? '' : 's'} after{' '}
								{index === 0
									? 'adding to sequence'
									: 'the last message'}{' '}
								at
							</span>
							<Input
								type="time"
								value={step.atTime || '09:00'}
								onChange={(e) =>
									onChange({
										...step,
										atTime: e.target.value || '09:00',
									})
								}
								className="h-7 w-[120px] rounded-full bg-background"
							/>
						</>
					)}
				</div>
			</div>

			{/* Template Selector Dialog */}
			{templates.length > 0 && (
				<TemplateSelector
					templates={templates}
					handleTemplateClick={handleTemplateClick}
					templatesExpanded={showTemplatesDialog}
					showEmojiPicker={false}
					toggleTemplates={() =>
						setShowTemplatesDialog(!showTemplatesDialog)
					}
					onOpenChange={handleDialogOpenChange}
					deleteTemplate={deleteTemplate}
					getUserTemplates={getUserTemplates}
					renderDialog
				/>
			)}
		</div>
	);
};

export default SequenceStepEditor;
